// MaturaGeometricParamClosedTask.js
// Trzywyrazowy ciąg geometryczny – szukamy parametru m (zadanie zamknięte).
//
// Warianty zachowują konstrukcję zadania CKE:
//   (4k, m, m-k), gdzie k jest małą liczbą naturalną.
// Warunek ciągu geometrycznego prowadzi do (m-2k)^2 = 0,
// więc zadanie ma jedno, całkowite rozwiązanie.
//
// Zawsze dokładnie JEDNA odpowiedź wśród A–D jest poprawna,
// wszystkie rozwiązania m są całkowite.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

/* ===== helpers ogólne (adaptacja z FindXForGeometricTripletTask) ===== */

function getRandomFromArray(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// proste formatowanie całkowitej liczby do LaTeX
function formatIntLatex(value) {
  return `${value}`;
}

/* ===== GENERATOR ===== */

/**
 * Wariant „oryginał” – matura podstawowa, czerwiec 2025, zadanie 16:
 *   (4, m, m-1) geometryczny  ⟺  m^2 = 4(m-1)  ⟺  (m-2)^2 = 0  ⟹  m = 2  (C).
 */
function createJune2025OriginalTask() {
  return {
    type: "MaturaGeometricParamClosedTask",
    data: {
      mode: "quad",
      a: "4",
      b: "m",
      c: "m - 1",
      p1: 0,
      p2: 1,
      p3: 1,
      aVal: 4,
      bVal: 0,
      cVal: -1,
      A_coef: 1,
      B_coef: -4,
      C_coef: 4,
      delta: 0,
      sqrtDelta: 0,
      allRoots: [2],
      validRoot: 2,
      fakeRoot: null,
      options: [-3, -2, 2, 3],
      correctIndex: 2,
      maxPoints: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 16,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  const k = getRandomFromArray([1, 2, 3, 4]);
  const correctM = 2 * k;
  const options = buildUniqueNonZeroOptions(correctM, [
    -(correctM + 1),
    -correctM,
    correctM + 1
  ]);

  return {
    type: "MaturaGeometricParamClosedTask",
    data: {
      mode: "quad",
      a: `${4 * k}`,
      b: "m",
      c: `m - ${k}`,
      p1: 0,
      p2: 1,
      p3: 1,
      aVal: 4 * k,
      bVal: 0,
      cVal: -k,
      A_coef: 1,
      B_coef: -4 * k,
      C_coef: 4 * k * k,
      delta: 0,
      sqrtDelta: 0,
      allRoots: [correctM],
      validRoot: correctM,
      fakeRoot: null,
      options,
      correctIndex: options.indexOf(correctM),
      maxPoints: 1
    }
  };
}

/* ===== pomocnicze tasowanie ===== */

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildUniqueNonZeroOptions(correctValue, preferredDistractors = []) {
  if (correctValue === 0) return null;

  const used = new Set([correctValue]);
  const distractors = [];

  const tryAdd = (value) => {
    if (!Number.isInteger(value) || value === 0 || used.has(value)) return false;
    used.add(value);
    distractors.push(value);
    return true;
  };

  preferredDistractors.forEach(tryAdd);

  let offset = 1;
  while (distractors.length < 3 && offset <= 12) {
    tryAdd(correctValue - offset);
    tryAdd(correctValue + offset);
    tryAdd(-correctValue - offset);
    tryAdd(-correctValue + offset);
    offset += 1;
  }

  if (distractors.length < 3) return null;

  return shuffleArray([correctValue, ...distractors.slice(0, 3)]);
}

/* ===== TREŚĆ (maturalna) ===== */

export function renderTask({ data }) {
  const { a, b, c } = data;

  return (
    <div style={{ textAlign: "left" }} className="space-y-2">
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Trzywyrazowy ciąg{" "}
        <InlineMath math={`(${a},\\ ${b},\\ ${c})`} /> jest geometryczny, gdy
        liczba <InlineMath math={"m"} /> jest równa:
      </p>
      <div style={{ marginTop: 8 }}>
        {data.options.map((val, idx) => {
          const letter = ["A", "B", "C", "D"][idx];
          const latex = formatIntLatex(val);
          return (
            <div key={letter} style={{ marginBottom: 8 }}>
              <InlineMath math={`${letter}.\\; ${latex}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===== INPUT (ABCD) ===== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const options = data.options;
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((_, idx) => ({
    value: idx,
    label: ["A", "B", "C", "D"][idx],
    textNode: ["A", "B", "C", "D"][idx]
  }));

  const handleChange = (idx) => {
    const letter = ["A", "B", "C", "D"][idx];
    const value = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: letter,
      latex: formatIntLatex(value),
      value
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

/* ===== TWOJA ODPOWIEDŹ ===== */

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    userAnswer.label == null ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

/* ===== SPRAWDZANIE ===== */

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

/* ===== POPRAWNA ODP. (krótka) ===== */

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const letter = ["A", "B", "C", "D"][correctIndex];
  const val = options[correctIndex];
  return <InlineMath math={`${letter}.\\; ${formatIntLatex(val)}`} />;
}

/* ===== WYJAŚNIENIE ===== */

export function renderExplanation({ data }) {
  const { a, b, c, cVal, validRoot } = data;
  const k = Math.abs(cVal);
  const fourK = 4 * k;
  const fourKSquared = 4 * k * k;
  const twoK = 2 * k;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Dla trzech kolejnych wyrazów ciągu
        geometrycznego <InlineMath math="(a,b,c)" /> zachodzi:
      </div>
      <BlockMath math={"b^2 = a c"} />

      <div>
        <strong>Krok 2.</strong> W naszym zadaniu mamy trójkę{" "}
        <InlineMath math={`(${a},\\ ${b},\\ ${c})`} />, więc:
      </div>
      <BlockMath math={`(${b})^2 = (${a})(${c})`} />
      <BlockMath math={`m^2 = ${fourK}(m-${k})`} />

      <div>
        <strong>Krok 3.</strong> Rozwijamy prawą stronę i przenosimy wszystkie
        wyrazy na lewą stronę:
      </div>
      <BlockMath math={`m^2 = ${fourK}m-${fourKSquared}`} />
      <BlockMath math={`m^2-${fourK}m+${fourKSquared}=0`} />

      <div>
        <strong>Krok 4.</strong> Lewa strona jest kwadratem różnicy:
      </div>
      <BlockMath math={`(m-${twoK})^2=0`} />
      <BlockMath math={`m=${formatIntLatex(validRoot)}`} />

      <div>
        <strong>Odpowiedź:</strong>{" "}
        <InlineMath
          math={`m = ${formatIntLatex(validRoot)}`}
        />
      </div>
    </div>
  );
}

/* ===== EXPORT DEFAULT ===== */

export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation
};
